import React, { useEffect, useRef } from 'react';

/**
 * Grant Math Fractal Lava Lamp Orbs
 * Formula: R = α⋅π/(2⋅(−θ+3π))
 * Uses Fibonacci sequence for sizing
 */
const GrantLavaLampOrbs = ({ width = 800, height = 600 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let time = 0;

    const centerX = width / 2;
    const centerY = height / 2;

    // Fibonacci sequence for orb sizing
    const fibonacci = [13, 21, 34, 55, 89, 144, 233];
    
    // Pastel lava lamp colors - MORE VIBRANT
    const pastelColors = [
      [255, 150, 200], // Bright pink
      [150, 200, 255], // Bright blue
      [200, 255, 150], // Bright green
      [255, 220, 150], // Bright peach
      [200, 150, 255], // Bright purple
      [255, 200, 150], // Bright coral
      [150, 255, 220], // Bright mint
      [220, 150, 255], // Bright lavender
    ];

    // Create orbs with Fibonacci sizing
    const orbs = [];
    for (let i = 0; i < 8; i++) {
      orbs.push({
        index: i,
        baseRadius: fibonacci[i % 7] * 0.4, // Use Fibonacci for size
        phaseOffset: (i * 2 * Math.PI) / 8,
        color: pastelColors[i],
        alpha: 1.618  // Golden ratio for alpha calculations
      });
    }

    const animate = () => {
      time += 0.008; // Very slow for lava lamp effect

      // Breathing cycle: grow to fill screen, then shrink to black hole
      const breatheCycle = Math.sin(time * 0.15) * 0.5 + 0.5; // 0 to 1
      const globalScale = 0.2 + (breatheCycle * 2.5); // Ranges from 0.2 (tiny) to 2.7 (huge)

      // Dark background with trail effect
      ctx.fillStyle = 'rgba(15, 23, 42, 0.05)';
      ctx.fillRect(0, 0, width, height);

      orbs.forEach((orb) => {
        // Grantitude Formula: R = α⋅π/(2⋅(−θ+3π))
        const theta = time + orb.phaseOffset;
        const denominator = 2 * (-theta + 3 * Math.PI);
        const formulaR = (orb.alpha * Math.PI) / denominator;
        
        // 3D position using spherical coordinates
        const radius = 150 + Math.sin(time * 0.3 + orb.phaseOffset) * 80;
        const orbTheta = time * 0.15 + orb.phaseOffset;
        const orbPhi = Math.sin(time * 0.1 + orb.phaseOffset) * Math.PI * 0.5;

        // 3D to 2D projection
        const x3d = radius * Math.sin(orbPhi) * Math.cos(orbTheta);
        const y3d = radius * Math.sin(orbPhi) * Math.sin(orbTheta);
        const z3d = radius * Math.cos(orbPhi);

        // Perspective projection
        const viewDistance = 400;
        const perspective = viewDistance / (viewDistance + z3d);
        const x = centerX + x3d * perspective;
        const y = centerY + y3d * perspective;

        // Orb size with breathing effect
        const breathe = 1 + Math.sin(time * 0.2 + orb.phaseOffset) * 0.15;
        const orbRadius = orb.baseRadius * perspective * breathe * globalScale;

        // Depth-based opacity (coming and going)
        const depthAlpha = Math.max(0.3, Math.min(0.8, (z3d + 200) / 400));

        // Create radial gradient for soft glowing sphere
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, orbRadius);
        const [r, g, b] = orb.color;
        
        gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${depthAlpha * 0.9})`);
        gradient.addColorStop(0.4, `rgba(${r}, ${g}, ${b}, ${depthAlpha * 0.7})`);
        gradient.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, ${depthAlpha * 0.4})`);
        gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

        // Draw orb
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, orbRadius, 0, Math.PI * 2);
        ctx.fill();

        // Soft outer glow
        ctx.shadowColor = `rgba(${r}, ${g}, ${b}, ${depthAlpha * 0.5})`;
        ctx.shadowBlur = orbRadius * 0.6;
        ctx.beginPath();
        ctx.arc(x, y, orbRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="rounded-lg"
      style={{ background: 'transparent' }}
    />
  );
};

export default GrantLavaLampOrbs;
